/**
 * One room in the billing grid.
 *
 * MEMOISED ON PURPOSE. The console opens on 200 rooms and an operator types
 * into it all day; when cell editing lands (5B) a keystroke must re-render one
 * row, not two hundred. That only holds if this component's props stay stable,
 * so it takes primitives and stable callbacks - never the `colors` object,
 * which `useColors()` rebuilds on every render and would defeat the memo.
 */
import { memo } from 'react'
import { Check, MoreVertical } from 'lucide-react'

import { useColors } from '../../../hooks/useColors'
import { formatMoney } from '../format'
import { BILLING_COLUMNS, FROZEN_OFFSETS } from './billingColumns'
import { blockingErrors, rowDisplay } from './billingRowModel'
import type { BillingRoomRow, RoomEntry } from './billingRowModel'

/** Which Wash & Iron cell an edit is aimed at. */
export type BillingField = 'weightKg' | 'ratePerKg' | 'pieces'

export interface BillingRowProps {
  row: BillingRoomRow
  /** The room's unsaved edit, if it has one. Stable identity unless edited. */
  draft?: RoomEntry
  selected: boolean
  /** Reveal validation only after a save attempt - never while still typing. */
  showErrors?: boolean
  onEdit: (roomId: string, field: BillingField, value: string) => void
  onToggleSelect: (roomId: string) => void
  /** Open the room's service editor - the whole-room view behind the Room cell. */
  onOpenRoom: (roomId: string) => void
  /** Open the item-line editor for one item-based service. */
  onOpenItems: (roomId: string, serviceSlug: string) => void
  onOpenMenu: (roomId: string, anchor: DOMRect) => void
  /** Row position, so keyboard movement can find the cell above and below. */
  rowIndex: number
}

/**
 * Status chip palette - the SAME tokens the shared Chip uses, so the grid and
 * the rest of the module can never disagree about what "Done" looks like.
 *
 * Tokens rather than hexes because the accessible value differs by theme: dark
 * ink on a pale wash in light mode, light ink on a stronger wash in dark. The
 * values and the contrast maths live in styles/theme.css.
 *
 * Deliberately NOT the Chip component: this row is memoised and renders 200+
 * times, and Chip calls useColors(), which returns a fresh object every call.
 * Static var() strings cost nothing.
 */
const STATUS_STYLE = {
  done: { label: 'Done', color: 'var(--chip-success-ink)', background: 'var(--chip-success-bg)' },
  in_progress: { label: 'In Progress', color: 'var(--chip-info-ink)', background: 'var(--chip-info-bg)' },
  pending: { label: 'Pending', color: 'var(--chip-warning-ink)', background: 'var(--chip-warning-bg)' },
} as const

const width = (key: string) => BILLING_COLUMNS.find((column) => column.key === key)?.width

function BillingRowComponent({
  row,
  rowIndex,
  draft,
  selected,
  showErrors = false,
  onEdit,
  onToggleSelect,
  onOpenRoom,
  onOpenItems,
  onOpenMenu,
}: BillingRowProps) {
  const colors = useColors()

  // What the row shows: the saved figures, with any unsaved edit laid over.
  const display = rowDisplay(row, draft)
  const errors = showErrors && draft ? blockingErrors(draft.weight) : {}

  // A frozen cell needs its own background, or the scrolling columns show
  // through it, and a z-index above them so it wins the overlap.
  const cellBase = {
    borderBottom: `1px solid ${colors.surface}`,
    padding: '6px 10px',
    fontSize: '12px',
    height: '38px',
  } as const

  const rowBackground = selected ? 'rgba(214,185,123,0.14)' : colors.bgCard

  const frozen = (key: string) => ({
    ...cellBase,
    position: 'sticky' as const,
    left: `${FROZEN_OFFSETS[key]}px`,
    zIndex: 1,
    background: rowBackground,
    width: `${width(key)}px`,
  })

  const status = STATUS_STYLE[display.status]

  return (
    <tr style={{ background: rowBackground }} data-room={row.roomNumber}>
      <td style={frozen('select')}>
        <input
          type="checkbox"
          checked={selected}
          aria-label={`Select room ${row.roomNumber}`}
          onChange={() => onToggleSelect(row.roomId)}
          style={{ width: '14px', height: '14px', accentColor: colors.textPrimary, cursor: 'pointer' }}
        />
      </td>

      <td style={frozen('room')}>
        <button
          type="button"
          className="font-body"
          onClick={() => onOpenRoom(row.roomId)}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            fontSize: '12px',
            fontWeight: 700,
            color: colors.textPrimary,
            cursor: 'pointer',
          }}
        >
          {row.roomNumber}
        </button>
      </td>

      <td style={frozen('status')}>
        {/* Quieter than the billing data on purpose (brief §12) - the numbers
            are what the operator is reading, not the chips. */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span
            className="font-body"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              borderRadius: '999px',
              padding: '2px 8px',
              fontSize: '11px',
              fontWeight: 600,
              color: status.color,
              background: status.background,
              whiteSpace: 'nowrap',
            }}
          >
            <span
              aria-hidden
              style={{ width: '5px', height: '5px', borderRadius: '999px', background: 'currentColor' }}
            />
            {status.label}
          </span>

          {/* brief §27: "small and unobtrusive". It answers one question - have
              these garments already got tags on them - and it is the only thing
              standing between a reload and a second set of WI-1/20s. */}
          {display.tagsPrinted ? (
            <span
              className="font-body"
              data-testid={`tags-printed-${row.roomNumber}`}
              title={
                display.tagsPrintedAt
                  ? `Tags printed ${new Date(display.tagsPrintedAt).toLocaleString('en-IN')}`
                  : 'Tags printed'
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                fontSize: '10px',
                fontWeight: 600,
                color: 'var(--chip-success-ink)',
                whiteSpace: 'nowrap',
              }}
            >
              <Check size={11} /> Tags printed
            </span>
          ) : null}
        </span>
      </td>

      <td style={cellBase}>
        <GridInput
          value={display.weightKg}
          onChange={(value) => onEdit(row.roomId, 'weightKg', value)}
          cell={`weight:${rowIndex}`}
          label={`Weight for room ${row.roomNumber}`}
          suffix="kg"
          width={68}
          invalid={Boolean(errors.weightKg)}
          title={errors.weightKg}
        />
      </td>

      <td style={cellBase}>
        <GridInput
          value={display.ratePerKg}
          onChange={(value) => onEdit(row.roomId, 'ratePerKg', value)}
          cell={`rate:${rowIndex}`}
          label={`Rate for room ${row.roomNumber}`}
          prefix="₹"
          width={64}
          invalid={Boolean(errors.ratePerKg)}
          title={errors.ratePerKg}
        />
      </td>

      <td style={cellBase}>
        <GridInput
          value={display.pieces}
          onChange={(value) => onEdit(row.roomId, 'pieces', value)}
          cell={`pieces:${rowIndex}`}
          label={`Pieces for room ${row.roomNumber}`}
          width={56}
          inputMode="numeric"
        />
      </td>

      <td style={cellBase}>
        <ItemCell
          count={display.dcItemCount}
          label={`Dry cleaning for room ${row.roomNumber}`}
          onClick={() => onOpenItems(row.roomId, 'dry-cleaning')}
        />
      </td>

      <td style={cellBase}>
        <ItemCell
          count={display.shoeItemCount}
          label={`Shoe care for room ${row.roomNumber}`}
          onClick={() => onOpenItems(row.roomId, 'shoe-care')}
        />
      </td>

      <td style={{ ...cellBase, textAlign: 'right', fontWeight: 700 }}>
        {display.total > 0 ? (
          formatMoney(display.total)
        ) : (
          <span style={{ color: colors.textMuted }}>-</span>
        )}
      </td>

      <td style={{ ...cellBase, textAlign: 'right' }}>
        <button
          type="button"
          aria-label={`Actions for room ${row.roomNumber}`}
          onClick={(event) => onOpenMenu(row.roomId, event.currentTarget.getBoundingClientRect())}
          style={{
            display: 'grid',
            placeItems: 'center',
            width: '26px',
            height: '26px',
            borderRadius: '6px',
            border: 'none',
            background: 'transparent',
            color: colors.textMuted,
            cursor: 'pointer',
          }}
        >
          <MoreVertical size={15} />
        </button>
      </td>
    </tr>
  )
}

/**
 * A borderless spreadsheet cell.
 *
 * Invisible until pointed at (a border on hover, gold on focus), because 200
 * rows of boxed inputs reads as a form, not a spreadsheet. The value is a
 * STRING all the way through: an operator typing "3." must still have "3." in
 * the box before they reach the 5, which coercing to a number would destroy.
 */
function GridInput({
  value,
  onChange,
  label,
  prefix,
  suffix,
  width,
  invalid,
  title,
  cell,
  inputMode = 'decimal',
}: {
  value: string
  onChange: (value: string) => void
  label: string
  prefix?: string
  suffix?: string
  width: number
  invalid?: boolean
  title?: string
  /** "<column>:<rowIndex>" - how the grid's key handler locates neighbours. */
  cell: string
  inputMode?: 'decimal' | 'numeric'
}) {
  const colors = useColors()

  return (
    <span style={{ position: 'relative', display: 'inline-block', width: `${width}px` }}>
      {prefix ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: '5px',
            top: '5px',
            fontSize: '10px',
            color: colors.textMuted,
            pointerEvents: 'none',
          }}
        >
          {prefix}
        </span>
      ) : null}
      <input
        value={value}
        aria-label={label}
        aria-invalid={invalid || undefined}
        title={title}
        data-cell={cell}
        inputMode={inputMode}
        placeholder="-"
        onChange={(event) => onChange(event.target.value)}
        // Hover / focus / invalid borders come from CSS, not React state - see
        // .b2b-grid-cell in theme.css.
        className="font-body b2b-grid-cell"
        style={{
          width: '100%',
          height: '28px',
          borderRadius: '5px',
          paddingLeft: prefix ? '14px' : '6px',
          paddingRight: suffix ? '18px' : '6px',
          fontSize: '11px',
          fontWeight: 600,
          color: colors.textPrimary,
        }}
      />
      {suffix ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            right: '5px',
            top: '6px',
            fontSize: '9px',
            color: colors.textMuted,
            pointerEvents: 'none',
          }}
        >
          {suffix}
        </span>
      ) : null}
    </span>
  )
}

/**
 * An item-based service's cell. The CELL is the button (not a separate "Edit"
 * link beside it), so the thing an operator points at is the thing that opens.
 */
function ItemCell({ count, label, onClick }: { count: number; label: string; onClick: () => void }) {
  const colors = useColors()

  return (
    <button
      type="button"
      className="font-body"
      aria-label={label}
      onClick={onClick}
      style={{
        minWidth: '70px',
        textAlign: 'left',
        border: '1px solid transparent',
        borderRadius: '6px',
        padding: '3px 6px',
        background: 'transparent',
        fontSize: '11px',
        fontWeight: 600,
        color: count > 0 ? colors.textPrimary : colors.textMuted,
        cursor: 'pointer',
      }}
    >
      {count > 0 ? `${count} item${count === 1 ? '' : 's'}` : '-'}
    </button>
  )
}

export default memo(BillingRowComponent)
